import React from 'react';
import { Link } from 'react-router-dom';
import { AppPagination, AppTable, AppTableActionButton } from '@/components/atoms';
import type { ClinicListItem } from '@/services/admin/clinics/types';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_BOTH } from '@/constants/serviceType';
import { Empty } from 'antd';
import { EMPTY_TEXT_FILTERED } from '@/constants';

type SortDir = 'ascend' | 'descend';

type SortField = 'clinics.clinic_number' | 'clinics.available_from';

type ClinicsMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type ClinicsTableSectionProps = {
  site: string;
  rows: ClinicListItem[];
  loading: boolean;
  meta: ClinicsMeta;
  defaultPerPage: number;

  canUseSiteTypeFeatures: boolean;
  tenantNameBySiteType: Map<string, string>;

  onSort: (field: SortField, dir: SortDir) => void;

  onChangePage: (page: number) => void;
  onDeleteClinic?: (id: number) => void;
};

const ClinicsTableSection: React.FC<ClinicsTableSectionProps> = ({
  site,
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures,
  tenantNameBySiteType,
  onSort,
  onChangePage,
}) => {
  return (
    <div className="bg-white border rounded-md">
      <AppTable
        columns={[
          {
            title: '治療院ID',
            dataIndex: 'clinic_number',
            key: 'clinic_number',
            width: 100,
            sorter: true,
            render: val => val,
          },
          ...(canUseSiteTypeFeatures
            ? [
                {
                  title: 'サイト区分',
                  key: 'site_type',
                  width: 150,
                  render: (row: ClinicListItem) => {
                    const siteType = row.site_type;
                    if (!siteType) return '';
                    return tenantNameBySiteType.get(siteType) || siteType;
                  },
                },
              ]
            : []),
          {
            title: '有効期限',
            dataIndex: 'available_from',
            key: 'available',
            width: 300,
            sorter: true,
            render: (_, row: ClinicListItem) =>
              formatPeriodDisplay(row.available_from, row.available_to),
          },
          {
            title: 'サービス',
            key: 'service_type',
            render: (row: ClinicListItem & { service_type?: 'r2' | 'a2' | 'both' | null }) => {
              const st = row.service_type?.toUpperCase();
              if (st === SERVICE_TYPE_R2) return 'マッサージ';
              if (st === SERVICE_TYPE_A2) return 'はり・きゅう';
              if (st === SERVICE_TYPE_BOTH) return 'はり・きゅう、マッサージ';
              return '';
            },
          },
          {
            title: '操作',
            key: 'actions',
            render: (row: ClinicListItem) => (
              <Link to={`/${site}/admin/clinics/${row.id}`}>
                <AppTableActionButton action="detail">詳細</AppTableActionButton>
              </Link>
            ),
          },
        ]}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        onChange={(_pagination, _filters, sorter) => {
          const sort = Array.isArray(sorter) ? sorter[0] : sorter;
          if (sort && sort.order) {
            const sortFieldKey =
              (sort.field as string) === 'clinic_number'
                ? 'clinics.clinic_number'
                : 'clinics.available_from';
            onSort(sortFieldKey as SortField, sort.order as SortDir);
          }
        }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />

      {meta && (
        <div className="flex justify-end p-4">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={p => onChangePage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default ClinicsTableSection;
